import React from "react";
import { createTheme } from "@mui/material/styles";
import { CheckBoxSharp, CheckBoxOutlineBlankSharp } from "@mui/icons-material";

const primary = { light: "#3d5ea7", main: "#0d3692", dark: "#092566" };
const secondary = { light: "#3D5EA7", main: "#0D3692", dark: "#0A2B74" };
const denseSize = "small" as "small" | "medium" | "large";
const notDenseSize = "large" as "small" | "medium" | "large";

const propertiesStyleDense = {
  size: denseSize,
  h1FontSize: "4rem",
  h2FontSize: "3rem",
  h3FontSize: "2.125rem",
  h4FontSize: "1.5rem",
  h5FontSize: "1.25rem",
  h6FontSize: "1rem",
  body1FontSize: "0.875rem",
  body2FontSize: "0.8rem",
  subtitle1FontSize: "0.875rem",
  subtitle2FontSize: "0.8rem",
  buttonFontSize: "0.8rem",
  captionFontSize: "0.7rem",
  overlineFontSize: "0.7rem"
};
const propertiesStyleNoDense = {
  size: notDenseSize,
  h1FontSize: "6rem",
  h2FontSize: "3.75rem",
  h3FontSize: "3rem",
  h4FontSize: "2.125rem",
  h5FontSize: "1.5rem",
  h6FontSize: "1.25rem",
  body1FontSize: "1rem",
  body2FontSize: "0.875rem",
  subtitle1FontSize: "1rem",
  subtitle2FontSize: "0.875rem",
  buttonFontSize: "0.875rem",
  captionFontSize: "0.75rem",
  overlineFontSize: "0.75rem"
};

let theme;

theme = (dense: boolean, muiLocale) => {
  theme = createTheme({ dense, palette: { mode: "light", primary, secondary } });

  const stylesAttributes = dense ? propertiesStyleDense : propertiesStyleNoDense;
  const textColor = theme.palette.text.primary;

  return createTheme(
    theme,
    {
      components: {
        MuiTooltip: { defaultProps: { disableInteractive: true }, styleOverrides: { tooltip: { borderRadius: 0 } } },
        // pas clean, car pour le Paper, il y a le prop square, mais le validator form ne semble pas permettre de styler le Paper
        MuiPaper: { defaultProps: { square: true }, styleOverrides: { rounded: { borderRadius: 0 } } },
        MuiButton: {
          defaultProps: { size: stylesAttributes.size },
          styleOverrides: { root: { borderRadius: 0, textTransform: "none", color: textColor } }
        },
        MuiTab: { styleOverrides: { root: { textTransform: "none" } } },
        MuiTouchRipple: { styleOverrides: { root: { borderRadius: 0 } } },
        MuiTypography: {
          styleOverrides: {
            body1: { fontSize: stylesAttributes.body1FontSize },
            body2: { fontSize: stylesAttributes.body2FontSize },
            h1: { fontSize: stylesAttributes.h1FontSize },
            h2: { fontSize: stylesAttributes.h2FontSize },
            h3: { fontSize: stylesAttributes.h3FontSize },
            h4: { fontSize: stylesAttributes.h4FontSize },
            h5: { fontSize: stylesAttributes.h5FontSize },
            h6: { fontSize: stylesAttributes.h6FontSize },
            subtitle1: { fontSize: stylesAttributes.subtitle1FontSize },
            subtitle2: { fontSize: stylesAttributes.subtitle2FontSize },
            button: { fontSize: stylesAttributes.buttonFontSize },
            caption: { fontSize: stylesAttributes.captionFontSize },
            overline: { fontSize: stylesAttributes.overlineFontSize }
          }
        },
        MuiToggleButton: {
          defaultProps: { size: stylesAttributes.size },
          styleOverrides: {
            root: { borderRadius: 0, textTransform: "none", color: textColor, "&$selected": { color: textColor } }
          }
        },
        MuiCheckbox: { defaultProps: { color: "secondary", checkedIcon: <CheckBoxSharp />, icon: <CheckBoxOutlineBlankSharp /> } },
        MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 0 } } },
        MuiList: { defaultProps: { dense, disablePadding: true } },
        MuiListItem: { defaultProps: { dense }, styleOverrides: { root: { ":focus-visible": { outline: "none" } } } },
        MuiMenuItem: { defaultProps: { dense } },
        MuiListItemIcon: { styleOverrides: { root: { color: textColor, minWidth: 35 } } },
        MuiFab: { styleOverrides: { root: { textTransform: "none" } }, defaultProps: { color: "secondary" } },
        MuiIconButton: { defaultProps: { size: stylesAttributes.size }, styleOverrides: { root: { color: textColor } } },
        MuiPopper: { defaultProps: { modifiers: [{ name: "preventOverflow", enabled: true, options: { rootBoundary: "viewport" } }] } },
        MuiTextField: { defaultProps: { variant: "standard", margin: dense ? "none" : "dense" } },
        MuiSelect: { defaultProps: { variant: "standard" } },
        MuiFormControl: { defaultProps: { margin: dense ? "none" : "dense" } },
        MuiInputLabel: { defaultProps: { variant: "standard" } },
        MuiBadge: { defaultProps: { color: "secondary", overlap: "circular" } },
        MuiAvatar: { defaultProps: { color: "secondary" } }
      }
    },
    muiLocale
  );
};

export default theme;
